import { useAuth } from '@/contexts/Auth';
import { topTitleContext } from '@/contexts/TopTitle';
import { fakeRequest, getOnlyNumbers } from '@/helpers/utils';
import { getLabelTaxRegime, listTaxRegime } from '@/interfaces/Shopmanagers';
import useGetMe from '@/store/Sessions/useGetMe';
import useGetTaxDataShopmanager, {
    TaxDataShopmanagerResponse,
} from '@/store/Shopmanager/useGetTaxDataShopmanager';
import useUpdateTaxDataShopmanager, {
    UpdateTaxData,
} from '@/store/Shopmanager/useUpdateTaxDataShopmanager';
import { DocumentEnum, MASKS_DOCUMENT, validateDocument } from '@/utils/doc';
import {
    ArrowLeftOutlined,
    CheckOutlined,
    CloseOutlined,
    EditOutlined,
} from '@ant-design/icons';
import {
    Button,
    Col,
    Form,
    Input,
    message,
    Row,
    Select,
    Switch,
    Upload,
} from 'antd';
import { UploadChangeParam } from 'antd/lib/upload';
import React, { useContext, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { Navigate, useNavigate } from 'react-router-dom';

function toFormData(data: UpdateTaxData): FormData {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
        if (value == null || value === '') return;

        formData.append(key, value instanceof File ? value : String(value));
    });

    return formData;
}

const TaxDataShopManager: React.FC = () => {
    const [fileList, setFileList] = useState<any>([]);
    const { title, titleValue } = useContext(topTitleContext);
    const navigate = useNavigate();
    const titlePage = 'Configurações Fiscais';
    const { data: taxData } = useGetTaxDataShopmanager();
    const { mutateAsync: performUpdateTaxData, isLoading } =
        useUpdateTaxDataShopmanager();
    const [form] = Form.useForm();

    const { data: extUser, isLoading: isLoadingUser } = useGetMe();
    const { isAssuming } = useAuth();

    if (isLoadingUser) return null; // ou loading/spinner

    if (!extUser?.shopManager?.can_change_tax_data || isAssuming) {
        return <Navigate to="/" replace />;
    }

    const getTaxRegimeSelectOptions = () => {
        const enumArr = listTaxRegime().map((value) => {
            return { value, label: getLabelTaxRegime(value) };
        });
        return [...enumArr];
    };

    const onChangeFile = ({ file, fileList }: UploadChangeParam) => {
        form.setFieldsValue({
            digitalCertificate: file.originFileObj,
        });
        if (fileList.length > 0) setFileList(fileList);
    };

    const onRemoveFile = () => {
        form.setFieldsValue({ image: null });
        setFileList([]);
        return false;
    };

    const formatValues = (values: UpdateTaxData) => {
        const formatted = { ...values };
        formatted.stateInscription = getOnlyNumbers(
            formatted.stateInscription || '',
        );
        formatted.municipalInscription = getOnlyNumbers(
            formatted?.municipalInscription || '',
        );

        if (formatted.digitalCertificatePassword === '******************') {
            formatted.digitalCertificatePassword = undefined;
        }

        return formatted;
    };

    const onSubmit = async (data: UpdateTaxData) => {
        try {
            const formattedData = formatValues(data);

            if (
                data?.digitalCertificatePassword &&
                data?.A1DigitalCertificate
            ) {
                formattedData.digitalCertificate = fileList[0]?.originFileObj;
            }
            const { A1DigitalCertificate, ...rest } = formattedData;
            const formData = toFormData(rest);

            await performUpdateTaxData(formData);
            message.success('Configurações atualizadas com sucesso!');
        } catch (error: any) {
            message.error(
                error.response?.data?.message ||
                    'Erro ao atualizar as configurações fiscais.',
            );
        }
    };

    const fillForm = (data: TaxDataShopmanagerResponse) => {
        const {
            focus_id: focusId,
            state_inscription: stateInscription,
            municipal_inscription: municipalInscription,
            tax_regime: taxRegime,
            emit_invoice_enabled: emitInvoiceEnabled,
            id_token_nfce_production: idTokenNfceProduction,
            digital_certificate_password: digitalCertificatePassword,
            csc_nfce_production: cscNfceProduction,
            focus_global_token: focusGlobalToken,
            accounting_cnpj: accountingCnpj,
            complementary_information_taxpayer:
                complementaryInformationTaxpayer,
        } = data;
        form.setFieldsValue({
            stateInscription,
            municipalInscription,
            taxRegime,
            emitInvoiceEnabled: !!emitInvoiceEnabled,
            digitalCertificatePassword: focusId
                ? '******************'
                : digitalCertificatePassword,
            idTokenNfceProduction,
            cscNfceProduction,
            focusGlobalToken,
            accountingCnpj,
            complementaryInformationTaxpayer,
        });
    };

    useEffect(() => {
        titleValue(titlePage);
    }, [title]);

    useEffect(() => {
        if (taxData) {
            fillForm(taxData);
        }
    }, [taxData]);

    const isEmitInvoice = Form.useWatch('emitInvoiceEnabled', form);
    const requiredEmitInvoice = isEmitInvoice && !taxData?.focus_id;
    return (
        <Row style={{ padding: '0 2rem' }}>
            <Col span={15}>
                <Col xs={24} sm={24} md={24} lg={20} xl={14} xxl={18}>
                    <Form layout="vertical" form={form} onFinish={onSubmit}>
                        <Form.Item
                            label="Inscrição estadual"
                            name="stateInscription"
                            rules={[
                                {
                                    min: 1,
                                    max: 20,
                                    message:
                                        'Inscrição estadual deve ter entre 1 e 20 dígitos',
                                    required: isEmitInvoice,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Inscrição municipal"
                            name="municipalInscription"
                            rules={[
                                {
                                    min: 1,
                                    max: 20,
                                    message:
                                        'Inscrição municipal deve ter entre 1 e 20 dígitos',
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Regime de tributação"
                            name="taxRegime"
                            rules={[
                                {
                                    type: 'enum',
                                    enum: listTaxRegime(),
                                    required: isEmitInvoice,
                                },
                            ]}
                        >
                            <Select options={getTaxRegimeSelectOptions()} />
                        </Form.Item>
                        <Form.Item
                            label="Emitir nota fiscal?"
                            name="emitInvoiceEnabled"
                            rules={[{ type: 'boolean' }]}
                        >
                            <Switch
                                checkedChildren={<CheckOutlined />}
                                unCheckedChildren={<CloseOutlined />}
                                checked={isEmitInvoice}
                            />
                        </Form.Item>
                        <Form.Item
                            label={`Certificado digital A1 ${
                                taxData?.focus_id
                                    ? ' - (1 Arquivo Submetido)'
                                    : ''
                            }`}
                            name="A1DigitalCertificate"
                            required={isEmitInvoice}
                            rules={[
                                {
                                    required: requiredEmitInvoice,
                                },
                            ]}
                        >
                            <Upload
                                fileList={fileList}
                                listType="picture-card"
                                maxCount={1}
                                onChange={onChangeFile}
                                onRemove={onRemoveFile}
                                customRequest={fakeRequest}
                                className="digitalCertificate"
                                accept=".pfx"
                            >
                                {' '}
                                <>
                                    <EditOutlined />
                                    <p
                                        style={{
                                            paddingLeft: '1rem',
                                        }}
                                    >
                                        Alterar
                                    </p>
                                </>
                            </Upload>
                        </Form.Item>
                        <Form.Item
                            label="Senha certificado digital"
                            name="digitalCertificatePassword"
                            required={isEmitInvoice}
                            rules={[
                                {
                                    required: requiredEmitInvoice,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Token ID (ID CSC NFCe produção)"
                            name="idTokenNfceProduction"
                            required={isEmitInvoice}
                            rules={[{ required: isEmitInvoice }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="CSC (NFCe produção)"
                            name="cscNfceProduction"
                            required={isEmitInvoice}
                            rules={[{ required: isEmitInvoice }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="Token principal produção (Focus NFe)"
                            name="focusGlobalToken"
                            tooltip="Acessar painel da Focus: Serviços > Painel API > Tokens: Token principal produção"
                            required={isEmitInvoice}
                            rules={[{ required: isEmitInvoice }]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item
                            label="CNPJ da Contabilidade"
                            name="accountingCnpj"
                            normalize={(value) => value?.toUpperCase()}
                            rules={[
                                {
                                    validator: async (_, value) => {
                                        if (!value) return;
                                        const error = validateDocument(
                                            value,
                                            DocumentEnum.CNPJ,
                                        );
                                        if (error) {
                                            throw new Error(error);
                                        }
                                    },
                                    required: false,
                                },
                            ]}
                        >
                            <InputMask mask={MASKS_DOCUMENT.cnpj}>
                                <Input />
                            </InputMask>
                        </Form.Item>
                        <Form.Item
                            label="Informações Complementares de interesse do Contribuinte"
                            name="complementaryInformationTaxpayer"
                            rules={[
                                {
                                    min: 1,
                                    max: 255,
                                    message:
                                        'Informações Complementares de interesse do Contribuinte deve ter entre 1 a 255 dígitos',
                                },
                            ]}
                        >
                            <Input.TextArea
                                rows={5}
                                style={{ resize: 'none' }}
                            />
                        </Form.Item>
                        <Row justify="center">
                            <Col span={8}>
                                <Button
                                    htmlType="reset"
                                    icon={<ArrowLeftOutlined />}
                                    style={{ width: '100%' }}
                                    onClick={() => navigate(-1)}
                                >
                                    Voltar
                                </Button>
                            </Col>
                            <Col offset={1} span={8}>
                                <Button
                                    htmlType="submit"
                                    className="default-btn"
                                    loading={isLoading}
                                >
                                    Salvar
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Col>
        </Row>
    );
};

export default TaxDataShopManager;
